// Single source of truth (client-side fallback + seed) for event-type config.
// The authoritative copy lives in Firestore `eventTypes/{type}`; this module
// seeds those docs and provides a safe fallback when a doc is missing.

import { DEFAULT_VIDEO_AI_CONFIG } from './videoAI'

export const DEFAULT_TYPE = 'regular'

// The closed set of admin config sections a type may hide.
export const TYPE_SECTION_IDS = [
  'themeColors',
  'prizeMode',
  'moderationSettings',
  'challengeBackground',
  'prizeBackground',
  'maxVideos',
  'logo',
  'splash',
  'analytics',
  'videoAI',
]

// Video AI settings are a *sibling* of `capabilities` and `hiddenSections`,
// deliberately not part of `defaults`: ChangeEventTypeDialog copies every
// `defaults` key onto the event document, and this config lives in a
// subcollection. The consequence, accepted in the design: changing an existing
// event's type does not rewrite its videoAI config.
const VIDEO_AI_OFF = {
  ...DEFAULT_VIDEO_AI_CONFIG,
  features: { ...DEFAULT_VIDEO_AI_CONFIG.features },
}

// Weddings run defaultModerated:true with capabilities.moderation:false — no
// human ever looks — so explicit detection without aiModeration would flag
// content and publish it anyway.
// Server copy: WEDDING_VIDEO_AI_FALLBACK in admin/functions/lib/events.js, used
// when eventTypes/wedding has no stored videoAI. Change one, change the other.
const VIDEO_AI_WEDDING = {
  ...VIDEO_AI_OFF,
  enabled: true,
  features: { ...VIDEO_AI_OFF.features, explicitContent: true },
  aiModeration: true,
}

// Universal starting shape for a newly-created event. A type's `defaults`
// override specific keys on top of this; `eventType` is stamped separately.
export const BASE_CREATE_DEFAULTS = {
  name: '',
  backgroundUrl: '',
  backgroundColor: '#000000',
  colorBg: '#0f172a',
  colorSurface: '#1e293b',
  colorPrimary: '#8b5cf6',
  colorText: '#f1f5f9',
  logoUrl: '',
  challengePrompt: { text: '', color: '#ffffff' },
  prizeBackgroundUrl: '',
  uploadBackgroundUrl: '',
  splashUrl: '',
  prizeBackgroundColor: null,
  prizePrompt: '',
  challenges: [],
  defaultModerated: false,
  maxSegmentDuration: 5,
  maxRecordingDuration: 15,
}

const FULL_CAPABILITIES = { moderation: true, prizes: true }
const FULL_EVENT_DEFAULTS = {
  defaultModerated: false,
  maxSegmentDuration: 5,
  maxRecordingDuration: 15,
}

// A starter challenge so every new event of a type begins with one challenge
// the organizer can rename or build on. Deterministic id keeps config stable.
// Shape matches the Challenges editor (`emptyChallenge` in Challenges.jsx).
export function starterChallenge(label, slug) {
  return { id: `${slug}-1`, label, color: '#00b5d8', textColor: '#000000', selfieCamera: true }
}

// A "full event" config (regular + the content types) differs only by
// label / tagColor / urlSlug, so build them from one template.
function fullType({ en, es, tagColor, slug, defaults }) {
  return {
    label: { en, es },
    tagColor,
    urlSlug: slug,
    entryFlow: 'login',
    adminRole: 'eventadmin',
    capabilities: { ...FULL_CAPABILITIES },
    hiddenSections: [],
    videoAI: { ...VIDEO_AI_OFF, features: { ...VIDEO_AI_OFF.features } },
    defaults: { ...FULL_EVENT_DEFAULTS, challenges: [starterChallenge(en, slug)], ...(defaults || {}) },
  }
}

export const DEFAULT_EVENT_TYPES = {
  regular: fullType({ en: 'Regular', es: 'Regular', tagColor: 'gray', slug: 'regular' }),
  wedding: {
    label: { en: 'Wedding', es: 'Boda' },
    tagColor: 'pink',
    urlSlug: 'wedding',
    entryFlow: 'nameDialog',
    adminRole: 'weddingadmin',
    capabilities: { moderation: false, prizes: false },
    hiddenSections: [
      'challengeBackground',
      'prizeBackground',
      'themeColors',
      'maxVideos',
      'prizeMode',
      'moderationSettings',
      'analytics',
    ],
    videoAI: VIDEO_AI_WEDDING,
    defaults: {
      receptionEnabled: true,
      defaultModerated: true,
      // Server copy: WEDDING_DEFAULTS in admin/functions/lib/events.js, used
      // when eventTypes/wedding stores no value. Change one, change the other.
      streamEnabled: true,
      galleryPublic: true,
      challenges: [starterChallenge('Wedding', 'wedding')],
      maxSegmentDuration: 5,
      maxRecordingDuration: 15,
    },
  },
  corporative: fullType({ en: 'Corporative', es: 'Corporativo', tagColor: 'blue', slug: 'corporative' }),
  music: fullType({ en: 'Music', es: 'Música', tagColor: 'purple', slug: 'music', defaults: { maxRecordingDuration: 30 } }),
  sports: fullType({ en: 'Sports', es: 'Deportes', tagColor: 'green', slug: 'sports' }),
  demo: fullType({ en: 'Demo', es: 'Demo', tagColor: 'orange', slug: 'demo' }),
  advertising: fullType({ en: 'Advertising', es: 'Publicidad', tagColor: 'red', slug: 'advertising' }),
}

// Copy only the keys present in `defaults` from `source`. Used by the
// type-change flow to compute exactly which preset-managed fields change.
export function pickPresetFields(source, defaults) {
  const out = {}
  for (const key of Object.keys(defaults || {})) {
    out[key] = source ? source[key] : undefined
  }
  return out
}
